/**
 * Structured output view — renders the agent's validated JSON output
 * as beautifully formatted sections.
 */

import {
  AlertTriangle,
  BarChart3,
  Brain,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  TrendingDown,
  Users,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { cn } from "@/utils/misc";
import { formatCurrency, formatMetricName } from "./shared";
import { SeverityBadge } from "./StatusBadges";

// ---------------------------------------------------------------------------
// Output shape
//
// The agent's structured output is stored as `v.any()` on
// `summarizationTasks.output` (and returned as `v.any()` from
// `getSummarizationTaskDetail`) because it is LLM-generated against a
// prompt-defined schema — nothing about its shape is guaranteed by the
// backend. These types document the fields this view knows how to render.
// The boundary is `unknown`, narrowed by the shallow `isStructuredOutput`
// guard; individual values are still rendered defensively.
// ---------------------------------------------------------------------------

interface KeyFinding {
  severity: string;
  finding?: string;
  category?: string;
  actionRequired?: string;
}

interface RedFlagEvidence {
  documentName?: string;
  excerpt?: string;
}

interface RedFlag {
  severity: string;
  indicator?: string;
  currentStatus?: string;
  firstAppeared?: string;
  lastObserved?: string;
  evidence?: RedFlagEvidence[];
}

interface UnusualItem {
  period?: string;
  description?: string;
  amount?: number;
  significance?: string;
}

interface ProfitAndLossAnalysis {
  revenueTrajectory?: string;
  costStructure?: string;
  profitabilityAssessment?: string;
  unusualItems?: UnusualItem[];
}

interface BalanceSheetAnalysis {
  assetComposition?: string;
  liabilityStructure?: string;
  equityDeterioration?: string;
  keyObservations?: string[];
}

interface CashFlowAnalysis {
  observations?: string[];
  operatingCashFlow?: (number | null)[];
  investingCashFlow?: (number | null)[];
  financingCashFlow?: (number | null)[];
  netCashFlow?: (number | null)[];
  closingCashBalance?: (number | null)[];
}

interface RelatedParty {
  name?: string;
  relationship?: string;
  receivableAmount?: number | null;
  payableAmount?: number | null;
  asPercentOfNetAssets?: number | null;
}

interface RelatedPartyAnalysis {
  parties?: RelatedParty[];
  totalRelatedPartyReceivables?: number | null;
  totalRelatedPartyPayables?: number | null;
  concerns?: string[];
}

interface TimelineMetric {
  name: string;
  values?: (number | null)[];
}

interface TimelineRatio extends TimelineMetric {
  formula?: string;
  benchmark?: string;
}

interface FinancialTimeline {
  granularity?: string;
  periods?: string[];
  metrics?: TimelineMetric[];
  ratios?: TimelineRatio[];
}

interface FundingAnalysis {
  periods?: string[];
  creditorsDue?: (number | null)[];
  totalEmployeeEntitlements?: (number | null)[];
  cashAvailable?: (number | null)[];
  availableAssets?: (number | null)[];
  fundingSurplusDeficit?: (number | null)[];
  observations?: string[];
}

interface DataGapDetail {
  description?: string;
  impact?: string;
  recommendation?: string;
}

interface DataAvailability {
  qualityAssessment?: string;
  documentsAnalysed?: number;
  documentsAvailable?: number;
  periodsWithData?: string[];
  periodsMissing?: string[];
  gaps?: (string | DataGapDetail)[];
}

interface StructuredOutput {
  status?: string;
  executiveSummary?: string;
  keyFindings?: KeyFinding[];
  financialRedFlags?: RedFlag[];
  insolvencyRedFlags?: RedFlag[];
  profitAndLossAnalysis?: ProfitAndLossAnalysis;
  balanceSheetAnalysis?: BalanceSheetAnalysis;
  cashFlowAnalysis?: CashFlowAnalysis;
  relatedPartyAnalysis?: RelatedPartyAnalysis;
  financialTimeline?: FinancialTimeline;
  fundingAnalysis?: FundingAnalysis;
  dataAvailability?: DataAvailability;
}

/**
 * Shallow structural guard: any plain object may carry the (mostly optional)
 * fields above. Leaf values are still guarded where the JSX renders them.
 */
function isStructuredOutput(value: unknown): value is StructuredOutput {
  return typeof value === "object" && value !== null;
}

// ---------------------------------------------------------------------------
// Main Output View
// ---------------------------------------------------------------------------

export function StructuredOutputView({
  output: rawOutput,
}: {
  output: unknown;
}) {
  if (!rawOutput) return null;
  const output: StructuredOutput = isStructuredOutput(rawOutput)
    ? rawOutput
    : {};

  return (
    <div className="flex flex-col gap-6 pb-8 pr-4">
      {/* Status badge */}
      {output.status && (
        <div className="flex items-center gap-3">
          <Badge
            variant={
              output.status === "complete"
                ? "default"
                : output.status === "partial"
                  ? "secondary"
                  : "destructive"
            }
            className="text-xs"
          >
            {output.status === "complete"
              ? "Complete Analysis"
              : output.status === "partial"
                ? "Partial Analysis"
                : "No Data"}
          </Badge>
          {output.dataAvailability?.qualityAssessment && (
            <Badge variant="outline" className="text-xs">
              Data quality: {output.dataAvailability.qualityAssessment}
            </Badge>
          )}
        </div>
      )}

      {/* Executive Summary */}
      {output.executiveSummary && (
        <OutputSection
          title="Executive Summary"
          icon={<Brain className="h-4 w-4" />}
        >
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {output.executiveSummary}
            </p>
          </div>
        </OutputSection>
      )}

      {/* Key Findings */}
      {output.keyFindings && output.keyFindings.length > 0 && (
        <OutputSection
          title={`Key Findings (${output.keyFindings.length})`}
          icon={<AlertTriangle className="h-4 w-4" />}
        >
          <div className="flex flex-col gap-2">
            {output.keyFindings.map((f, i) => (
              <div
                key={i}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3",
                  (f.severity === "critical" || f.severity === "high") &&
                    "border-destructive/30 bg-destructive/5",
                  f.severity === "medium" &&
                    "border-amber-500/20 bg-amber-50/30 dark:bg-amber-950/10",
                )}
              >
                <SeverityBadge severity={f.severity} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed">{f.finding}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    {f.category && (
                      <Badge variant="outline" size="sm">
                        {f.category.replace(/_/g, " ")}
                      </Badge>
                    )}
                  </div>
                  {f.actionRequired && (
                    <p className="mt-1.5 flex items-start gap-1 text-xs text-muted-foreground">
                      <ChevronRight className="mt-0.5 h-3 w-3 shrink-0" />
                      {f.actionRequired}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </OutputSection>
      )}

      {/* Financial Red Flags */}
      <RedFlagsSection output={output} />

      {/* P&L Analysis */}
      {output.profitAndLossAnalysis && (
        <OutputSection
          title="Profit & Loss Analysis"
          icon={<BarChart3 className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <AnalysisField
              label="Revenue Trajectory"
              value={output.profitAndLossAnalysis.revenueTrajectory}
            />
            <AnalysisField
              label="Cost Structure"
              value={output.profitAndLossAnalysis.costStructure}
            />
            <AnalysisField
              label="Profitability Assessment"
              value={output.profitAndLossAnalysis.profitabilityAssessment}
            />
            {output.profitAndLossAnalysis.unusualItems &&
              output.profitAndLossAnalysis.unusualItems.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Unusual Items
                  </p>
                  <div className="space-y-2">
                    {output.profitAndLossAnalysis.unusualItems.map(
                      (item, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 rounded border bg-muted/20 p-2.5 text-sm"
                        >
                          <Badge
                            variant="outline"
                            size="sm"
                            className="mt-0.5 shrink-0"
                          >
                            {item.period}
                          </Badge>
                          <div>
                            <p>{item.description}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              Amount: ${item.amount?.toLocaleString()} —{" "}
                              {item.significance}
                            </p>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>
        </OutputSection>
      )}

      {/* Balance Sheet Analysis */}
      {output.balanceSheetAnalysis && (
        <OutputSection
          title="Balance Sheet Analysis"
          icon={<BarChart3 className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <AnalysisField
              label="Asset Composition"
              value={output.balanceSheetAnalysis.assetComposition}
            />
            <AnalysisField
              label="Liability Structure"
              value={output.balanceSheetAnalysis.liabilityStructure}
            />
            <AnalysisField
              label="Equity Deterioration"
              value={output.balanceSheetAnalysis.equityDeterioration}
            />
            {output.balanceSheetAnalysis.keyObservations &&
              output.balanceSheetAnalysis.keyObservations.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Key Observations
                  </p>
                  <ul className="space-y-1">
                    {output.balanceSheetAnalysis.keyObservations.map(
                      (obs, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="mt-1 shrink-0 text-muted-foreground">
                            &bull;
                          </span>
                          <span>{obs}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </div>
        </OutputSection>
      )}

      {/* Cash Flow Analysis */}
      {output.cashFlowAnalysis && (
        <OutputSection
          title="Cash Flow Analysis"
          icon={<TrendingDown className="h-4 w-4" />}
        >
          <div className="space-y-3">
            {output.cashFlowAnalysis.observations &&
              output.cashFlowAnalysis.observations.length > 0 && (
                <ul className="space-y-1">
                  {output.cashFlowAnalysis.observations.map((obs, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1 shrink-0 text-muted-foreground">
                        &bull;
                      </span>
                      <span>{obs}</span>
                    </li>
                  ))}
                </ul>
              )}
            {output.financialTimeline?.periods && (
              <CashFlowTable
                periods={output.financialTimeline.periods}
                cashFlow={output.cashFlowAnalysis}
              />
            )}
          </div>
        </OutputSection>
      )}

      {/* Related Party Analysis */}
      {output.relatedPartyAnalysis && (
        <RelatedPartySection
          relatedPartyAnalysis={output.relatedPartyAnalysis}
        />
      )}

      {/* Financial Timeline */}
      {output.financialTimeline && (
        <FinancialTimelineSection timeline={output.financialTimeline} />
      )}

      {/* Funding Analysis */}
      {output.fundingAnalysis && (
        <FundingSection fundingAnalysis={output.fundingAnalysis} />
      )}

      {/* Data Availability */}
      {output.dataAvailability && (
        <DataAvailabilitySection dataAvailability={output.dataAvailability} />
      )}

      {/* Raw output for debugging */}
      <Collapsible>
        <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ChevronDown className="h-3 w-3" />
          Raw JSON output
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="mt-2 max-h-[50vh] overflow-auto rounded-md bg-muted/30 p-3 text-[11px] leading-relaxed">
            {JSON.stringify(rawOutput, null, 2)}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section Components
// ---------------------------------------------------------------------------

function OutputSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="text-primary/70">{icon}</span>
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function AnalysisField({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm leading-relaxed">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Red Flags
// ---------------------------------------------------------------------------

function RedFlagsSection({ output }: { output: StructuredOutput }) {
  const flags = output.financialRedFlags ?? output.insolvencyRedFlags;
  if (!flags?.length) return null;

  return (
    <OutputSection
      title={`Financial Red Flags (${flags.length})`}
      icon={<TrendingDown className="h-4 w-4" />}
    >
      <div className="flex flex-col gap-3">
        {flags.map((flag, i) => (
          <div
            key={i}
            className={cn(
              "rounded-lg border p-4",
              (flag.severity === "critical" || flag.severity === "high") &&
                "border-destructive/30 bg-destructive/5",
              flag.severity === "medium" && "border-amber-500/20",
            )}
          >
            <div className="mb-2 flex items-center gap-2">
              <SeverityBadge severity={flag.severity} />
              <span className="flex-1 text-sm font-medium">
                {flag.indicator}
              </span>
              <Badge variant="outline" size="sm" className="shrink-0">
                {flag.currentStatus}
              </Badge>
            </div>
            <div className="mb-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>
                <span className="font-medium">First appeared:</span>{" "}
                {flag.firstAppeared}
              </div>
              <div>
                <span className="font-medium">Last observed:</span>{" "}
                {flag.lastObserved}
              </div>
            </div>
            {flag.evidence && flag.evidence.length > 0 && (
              <div className="mt-2 border-t border-muted/50 pt-2">
                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Evidence
                </p>
                {flag.evidence.map((e, j) => (
                  <div
                    key={j}
                    className="mb-1 flex items-start gap-2 text-xs text-muted-foreground last:mb-0"
                  >
                    <FileText className="mt-0.5 h-3 w-3 shrink-0" />
                    <div>
                      <span className="font-medium">{e.documentName}</span>
                      {e.excerpt && (
                        <span className="italic">
                          {" "}
                          — &ldquo;{e.excerpt}&rdquo;
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </OutputSection>
  );
}

// ---------------------------------------------------------------------------
// Related Party
// ---------------------------------------------------------------------------

function RelatedPartySection({
  relatedPartyAnalysis,
}: {
  relatedPartyAnalysis: RelatedPartyAnalysis;
}) {
  return (
    <OutputSection
      title="Related Party Analysis"
      icon={<Users className="h-4 w-4" />}
    >
      <div className="space-y-3">
        {relatedPartyAnalysis.parties?.map((party, i) => (
          <div key={i} className="rounded-lg border p-3">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-sm font-medium">{party.name}</span>
              <Badge variant="outline" size="sm">
                {party.relationship}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              {party.receivableAmount != null && (
                <div>
                  <p className="text-muted-foreground">Receivable</p>
                  <p className="font-medium tabular-nums">
                    ${party.receivableAmount.toLocaleString()}
                  </p>
                </div>
              )}
              {party.payableAmount != null && (
                <div>
                  <p className="text-muted-foreground">Payable</p>
                  <p className="font-medium tabular-nums">
                    ${party.payableAmount.toLocaleString()}
                  </p>
                </div>
              )}
              {party.asPercentOfNetAssets != null && (
                <div>
                  <p className="text-muted-foreground">% of Net Assets</p>
                  <p className="font-medium tabular-nums">
                    {party.asPercentOfNetAssets.toFixed(1)}%
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        {(relatedPartyAnalysis.totalRelatedPartyReceivables != null ||
          relatedPartyAnalysis.totalRelatedPartyPayables != null) && (
          <div className="flex gap-4 rounded border bg-muted/20 p-3 text-sm">
            {relatedPartyAnalysis.totalRelatedPartyReceivables != null && (
              <div>
                <span className="text-xs text-muted-foreground">
                  Total Receivables:{" "}
                </span>
                <span className="font-medium tabular-nums">
                  $
                  {relatedPartyAnalysis.totalRelatedPartyReceivables.toLocaleString()}
                </span>
              </div>
            )}
            {relatedPartyAnalysis.totalRelatedPartyPayables != null && (
              <div>
                <span className="text-xs text-muted-foreground">
                  Total Payables:{" "}
                </span>
                <span className="font-medium tabular-nums">
                  $
                  {relatedPartyAnalysis.totalRelatedPartyPayables.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
        {relatedPartyAnalysis.concerns &&
          relatedPartyAnalysis.concerns.length > 0 && (
            <div className="rounded border border-amber-500/20 bg-amber-50/30 p-3 dark:bg-amber-950/10">
              <p className="mb-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                Concerns
              </p>
              {relatedPartyAnalysis.concerns.map((c, i) => (
                <p
                  key={i}
                  className="mb-1 flex items-start gap-1.5 text-xs text-amber-800/80 last:mb-0 dark:text-amber-300/80"
                >
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {c}
                </p>
              ))}
            </div>
          )}
      </div>
    </OutputSection>
  );
}

// ---------------------------------------------------------------------------
// Financial Timeline
// ---------------------------------------------------------------------------

function FinancialTimelineSection({
  timeline,
}: {
  timeline: FinancialTimeline;
}) {
  return (
    <OutputSection
      title="Financial Timeline"
      icon={<BarChart3 className="h-4 w-4" />}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Badge variant="outline" size="sm">
            {timeline.granularity}
          </Badge>
          <span>
            {timeline.periods?.length} periods: {timeline.periods?.join(", ")}
          </span>
        </div>

        {/* Metrics table */}
        {timeline.metrics && timeline.metrics.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Financial Metrics
            </p>
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="px-3 py-2 text-left font-medium">Metric</th>
                    {timeline.periods?.map((p) => (
                      <th
                        key={p}
                        className="whitespace-nowrap px-3 py-2 text-right font-medium"
                      >
                        {p}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeline.metrics.map((m, i) => (
                    <tr
                      key={i}
                      className="border-t border-muted/30 hover:bg-muted/20"
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 font-medium text-muted-foreground">
                        {formatMetricName(m.name)}
                      </td>
                      {m.values?.map((v, j) => (
                        <td
                          key={j}
                          className={cn(
                            "px-3 py-1.5 text-right font-mono text-[11px] tabular-nums",
                            v !== null && v < 0 && "text-destructive",
                          )}
                        >
                          {v === null ? (
                            <span className="text-muted-foreground/50">
                              &mdash;
                            </span>
                          ) : (
                            formatCurrency(v)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ratios table */}
        {timeline.ratios && timeline.ratios.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Financial Ratios
            </p>
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="px-3 py-2 text-left font-medium">Ratio</th>
                    {timeline.periods?.map((p) => (
                      <th
                        key={p}
                        className="whitespace-nowrap px-3 py-2 text-right font-medium"
                      >
                        {p}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium">
                      Benchmark
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.ratios.map((r, i) => (
                    <tr
                      key={i}
                      className="border-t border-muted/30 hover:bg-muted/20"
                      title={r.formula}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 font-medium text-muted-foreground">
                        {formatMetricName(r.name)}
                      </td>
                      {r.values?.map((v, j) => (
                        <td
                          key={j}
                          className={cn(
                            "px-3 py-1.5 text-right font-mono text-[11px] tabular-nums",
                            v !== null && v < 0 && "text-destructive",
                          )}
                        >
                          {v === null ? (
                            <span className="text-muted-foreground/50">
                              &mdash;
                            </span>
                          ) : (
                            v.toFixed(2)
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right text-[10px] italic text-muted-foreground">
                        {r.benchmark ?? ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </OutputSection>
  );
}

// ---------------------------------------------------------------------------
// Cash Flow Table
// ---------------------------------------------------------------------------

function CashFlowTable({
  periods,
  cashFlow,
}: {
  periods: string[];
  cashFlow: CashFlowAnalysis;
}) {
  const rows = [
    { label: "Operating", values: cashFlow.operatingCashFlow },
    { label: "Investing", values: cashFlow.investingCashFlow },
    { label: "Financing", values: cashFlow.financingCashFlow },
    { label: "Net Cash Flow", values: cashFlow.netCashFlow, bold: true },
    {
      label: "Closing Balance",
      values: cashFlow.closingCashBalance,
      bold: true,
    },
  ].filter((r) => r.values?.some((v) => v !== null));

  if (rows.length === 0) return null;

  return (
    <div className="overflow-auto rounded-lg border">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/50">
            <th className="px-3 py-2 text-left font-medium">Cash Flow</th>
            {periods.map((p) => (
              <th
                key={p}
                className="whitespace-nowrap px-3 py-2 text-right font-medium"
              >
                {p}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={cn(
                "border-t border-muted/30",
                row.bold && "bg-muted/20 font-medium",
              )}
            >
              <td className="px-3 py-1.5 text-muted-foreground">{row.label}</td>
              {row.values?.map((v, j) => (
                <td
                  key={j}
                  className={cn(
                    "px-3 py-1.5 text-right font-mono text-[11px] tabular-nums",
                    v !== null && v < 0 && "text-destructive",
                  )}
                >
                  {v === null ? "&mdash;" : formatCurrency(v)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Funding Section
// ---------------------------------------------------------------------------

function FundingSection({
  fundingAnalysis,
}: {
  fundingAnalysis: FundingAnalysis;
}) {
  return (
    <OutputSection
      title="Funding Position"
      icon={<Database className="h-4 w-4" />}
    >
      <div className="space-y-3">
        {fundingAnalysis.periods && fundingAnalysis.periods.length > 0 && (
          <div className="overflow-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Item</th>
                  {fundingAnalysis.periods.map((p) => (
                    <th
                      key={p}
                      className="whitespace-nowrap px-3 py-2 text-right font-medium"
                    >
                      {p}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-muted/30">
                  <td className="px-3 py-1.5 text-muted-foreground">
                    Creditors Due
                  </td>
                  {(
                    fundingAnalysis.creditorsDue ??
                    fundingAnalysis.totalEmployeeEntitlements
                  )?.map((v, j) => (
                    <td
                      key={j}
                      className="px-3 py-1.5 text-right font-mono text-[11px] tabular-nums"
                    >
                      {v === null ? "&mdash;" : formatCurrency(v)}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-muted/30">
                  <td className="px-3 py-1.5 text-muted-foreground">
                    Cash Available
                  </td>
                  {(
                    fundingAnalysis.cashAvailable ??
                    fundingAnalysis.availableAssets
                  )?.map((v, j) => (
                    <td
                      key={j}
                      className="px-3 py-1.5 text-right font-mono text-[11px] tabular-nums"
                    >
                      {v === null ? "&mdash;" : formatCurrency(v)}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-muted/50 font-medium">
                  <td className="px-3 py-1.5">Surplus / (Deficit)</td>
                  {fundingAnalysis.fundingSurplusDeficit?.map((v, j) => (
                    <td
                      key={j}
                      className={cn(
                        "px-3 py-1.5 text-right font-mono text-[11px] tabular-nums",
                        v !== null && v < 0 && "text-destructive",
                      )}
                    >
                      {v === null ? "&mdash;" : formatCurrency(v)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {fundingAnalysis.observations &&
          fundingAnalysis.observations.length > 0 && (
            <ul className="space-y-1">
              {fundingAnalysis.observations.map((obs, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="mt-1 shrink-0 text-muted-foreground">
                    &bull;
                  </span>
                  <span>{obs}</span>
                </li>
              ))}
            </ul>
          )}
      </div>
    </OutputSection>
  );
}

// ---------------------------------------------------------------------------
// Data Availability
// ---------------------------------------------------------------------------

function DataAvailabilitySection({
  dataAvailability,
}: {
  dataAvailability: DataAvailability;
}) {
  return (
    <OutputSection
      title="Data Availability"
      icon={<FileText className="h-4 w-4" />}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Badge
            variant={
              dataAvailability.qualityAssessment === "comprehensive"
                ? "default"
                : dataAvailability.qualityAssessment === "adequate"
                  ? "secondary"
                  : "destructive"
            }
            className="text-xs"
          >
            {dataAvailability.qualityAssessment}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {dataAvailability.documentsAnalysed} of{" "}
            {dataAvailability.documentsAvailable} documents analysed
          </span>
        </div>

        {dataAvailability.periodsWithData &&
          dataAvailability.periodsWithData.length > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground">Periods with data: </span>
              <span>{dataAvailability.periodsWithData.join(", ")}</span>
            </div>
          )}

        {dataAvailability.periodsMissing &&
          dataAvailability.periodsMissing.length > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground">Periods missing: </span>
              <span className="text-amber-600">
                {dataAvailability.periodsMissing.join(", ")}
              </span>
            </div>
          )}

        {dataAvailability.gaps && dataAvailability.gaps.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Data Gaps
            </p>
            {dataAvailability.gaps.map((gap, i) => {
              const detail = typeof gap === "string" ? undefined : gap;
              return (
                <div
                  key={i}
                  className="rounded-lg border border-amber-500/20 bg-amber-50/30 p-3 text-xs dark:bg-amber-950/10"
                >
                  <p className="font-medium">
                    {typeof gap === "string" ? gap : gap.description}
                  </p>
                  {detail?.impact && (
                    <p className="mt-0.5 text-muted-foreground">
                      Impact: {detail.impact}
                    </p>
                  )}
                  {detail?.recommendation && (
                    <p className="mt-0.5 text-primary/80">
                      Recommendation: {detail.recommendation}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OutputSection>
  );
}
